import { z } from 'zod'
import { OrderSchema, OrderStatusSchema } from './schemas'

const quantity = z
  .union([z.number(), z.string()])
  .transform(value => (typeof value === 'string' ? Number(value) : value))
  .pipe(z.number().finite().positive().multipleOf(0.0001))

const nonnegativeQuantity = z
  .union([z.number(), z.string()])
  .transform(value => (typeof value === 'string' ? Number(value) : value))
  .pipe(z.number().nonnegative())

export const OrderCancellationLineRequestSchema = z.object({
  itemSequenceNumber: z.number().int().positive(),
  quantity,
})

export const OrderCancellationRequestSchema = z.discriminatedUnion('scope', [
  z.object({
    scope: z.literal('ORDER'),
    reason: z.string().trim().min(1),
    remarks: z.string().trim().optional(),
  }),
  z.object({
    scope: z.literal('LINES'),
    lines: z.array(OrderCancellationLineRequestSchema).min(1),
    reason: z.string().trim().min(1),
    remarks: z.string().trim().optional(),
  }),
])
export type OrderCancellationRequest = z.infer<typeof OrderCancellationRequestSchema>

export const OrderCancellationLineResultSchema = z.object({
  itemSequenceNumber: z.number().int(),
  quantity: quantity.optional(),
  manualCancelledQuantity: nonnegativeQuantity.optional(),
  cancellableQuantity: nonnegativeQuantity.optional(),
})

/**
 * The backend is being implemented in parallel. Vision conventionally returns
 * either the updated aggregate or a mutation result carrying it, so accept only
 * those two explicit shapes and normalize them at the query boundary.
 */
export const OrderCancellationResponseSchema = z.union([
  OrderSchema,
  z.object({
    externalOrderNumber: z.string(),
    status: OrderStatusSchema,
    scope: z.enum(['ORDER', 'LINES']).optional(),
    cancelledQuantity: nonnegativeQuantity.optional(),
    lines: z.array(OrderCancellationLineResultSchema).optional(),
    order: OrderSchema.optional(),
  }),
])
export type OrderCancellationResponse = z.infer<typeof OrderCancellationResponseSchema>

export function orderFromCancellationResponse(
  response: OrderCancellationResponse,
): z.infer<typeof OrderSchema> | undefined {
  if ('id' in response) return response
  return response.order
}

export const ORDER_CANCELLATION_ERROR_CODES = [
  'NOT_ACKNOWLEDGED',
  'NOTHING_TO_CANCEL',
  'QUANTITY_EXCEEDS_CANCELLABLE',
  'ALREADY_CANCELLED',
  'IDEMPOTENCY_CONFLICT',
  'TERMINAL_ORDER',
  'LINE_NOT_FOUND',
  'PO_CANCELLATION_DISABLED',
  'CONFLICT',
  'FORBIDDEN',
  'UNKNOWN',
] as const
export type OrderCancellationErrorCode = (typeof ORDER_CANCELLATION_ERROR_CODES)[number]
